/* eslint-disable */
import { sentenceCase } from 'change-case';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Button,
  Tooltip,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken, setAuthUser } from '../redux/reducers/authSlice';
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import { UserListHead, UserListToolbar, UserMoreMenu } from '../sections/@dashboard/user/index';
import { authApiCall } from '../services/http';
import { endpoints } from '../config';
import UserMetricsDialog from '../layouts/dialogs/UserMetricsDialog';
import Iconify from 'src/components/Iconify';
import MeetingStatusTooltip from 'src/components/MeetingStatusTooltip';
import CreateNewUserFormDialog from 'src/layouts/dialogs/CreateNewUserFormDialog';
import SubscriptionDetailsDialog from 'src/layouts/dialogs/SubscriptionDetailsDialog';
import SubscriptionUsersDialog from 'src/pages/subscriptions/components/SubscriptionUsersDialog';
import EditUserDialog from 'src/layouts/dialogs/EditUserDialog';
import copyToClipboard from 'src/utils/copyToClipboard';
import { subscriptionStatus, stripeSubscriptionUrl } from 'src/helpers/subscriptions';
import { languageName } from 'src/helpers/languages';
import { isActivatedUser, processedMeetingCount } from 'src/helpers/meetingStatus';
import useAuthApiCall from '../hooks/useAuthApiCall';
import UsersGraph from './dashboardApp/components/UsersGraph';
import UseCasePieChart from './dashboardApp/components/UseCasePieChart';
import ChartCardSkeleton from './dashboardApp/components/ChartCardSkeleton';
import ActivationCohortCard from './dashboardApp/components/ActivationCohortCard';

// `mobileHide`: ocultar la columna en xs/sm para que la tabla quepa mejor en móvil.
const TABLE_HEAD = [
  { id: 'createdAt', label: 'Fecha', alignRight: false, mobileHide: true },
  { id: 'user', label: 'Usuario', alignRight: false },
  { id: 'activity', label: 'Actividad (7d · mes · prev · total)', alignRight: false, mobileHide: true },
  { id: 'workspace', label: 'Workspace', alignRight: false, mobileHide: true },
  { id: 'subscription', label: 'Suscripción', alignRight: false },
  { id: 'actions', label: '', alignRight: true },
];

const hideOnMobile = { display: { xs: 'none', md: 'table-cell' } };

// Una de las 4 cifras de la celda de actividad: nº de reuniones vivas creadas en la ventana,
// con un sufijo rojo "−N" cuando hay borradas en esa misma ventana. El tooltip combina ambos
// desgloses por estado (vivas + eliminadas). `deleted`/`deletedStatusCounts` son opcionales.
function ActivityCount({ count, statusCounts, deleted, deletedStatusCounts, title, deletedTitle, bold }) {
  const hasTip = statusCounts || deletedStatusCounts;
  return (
    <MeetingStatusTooltip
      statusCounts={statusCounts}
      deletedStatusCounts={deletedStatusCounts}
      title={title}
      deletedTitle={deletedTitle}
    >
      <Stack direction="row" alignItems="baseline" spacing={0.25} sx={{ ...(hasTip && { cursor: 'help' }) }}>
        <Typography variant="body2" sx={{ ...(bold && { fontWeight: 600 }) }}>
          {count ?? 0}
        </Typography>
        {deleted > 0 && (
          <Typography variant="caption" sx={{ color: 'error.main', fontVariantNumeric: 'tabular-nums' }}>
            −{deleted}
          </Typography>
        )}
      </Stack>
    </MeetingStatusTooltip>
  );
}

const colorMap = {
  sales: 'error',
  clients: 'success',
  hiring: 'info',
  training: 'primary',
  team: 'warning',
  other: 'default',
  null: 'default',
};

const MEETINGS_PRESETS = [
  { value: 0, label: 'Cualquiera' },
  { value: 1, label: '≥1' },
  { value: 5, label: '≥5' },
  { value: 10, label: '≥10' },
  { value: 15, label: '≥15' },
  { value: 20, label: '≥20' },
];

const USE_CASE_OPTIONS = [
  { value: '', label: 'Todos', color: 'default' },
  { value: 'sales', label: 'Sales', color: 'error' },
  { value: 'clients', label: 'Clients', color: 'success' },
  { value: 'hiring', label: 'Hiring', color: 'info' },
  { value: 'training', label: 'Training', color: 'primary' },
  { value: 'team', label: 'Team', color: 'warning' },
  { value: 'other', label: 'Other', color: 'default' },
];

export default function TableUser() {
  const [userList, setuserList] = useState([]);
  const [page, setPage] = useState(0);
  const [order] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [userMetricsDialogOpen, setUserMetricsDialogOpen] = useState(false);
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [userMetricsCurrentId, setUserMetricsCurrentId] = useState(-1);
  const [meetingsPerMonthSearchValue, setMeetingPerMonthSearchValue] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // const [filterByMonth, setFilterByMonth] = useState(true);
  const [useCaseFilter, setUseCaseFilter] = useState(''); // '' = todos
  // Filtros efectivamente aplicados (lo que se manda al backend). Separados del input vivo
  // para que el fetch solo se dispare al pulsar Buscar/Limpiar, no en cada tecla.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedUseCase, setAppliedUseCase] = useState('');
  const [appliedMeetingNumber, setAppliedMeetingNumber] = useState(0);
  const [subscriptionsById, setSubscriptionsById] = useState({});
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subDialogSubscription, setSubDialogSubscription] = useState(null);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  // Drill-in de suscripción/seats reutilizando el mismo diálogo de la página de Suscripciones.
  const [seatDialogSub, setSeatDialogSub] = useState(null);
  const seatDialogDirty = useRef(false);
  const authToken = useSelector((state) => state.auth.authToken);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { json: kpisRes, error: kpisError } = useAuthApiCall({
    method: 'GET',
    endpoint: endpoints.kpis,
    path: '',
  });
  // Skeletons solo mientras la llamada está en vuelo: si falla, no dejamos placeholders eternos.
  const kpisLoading = !kpisRes && !kpisError;

  const getColor = (useCase) => colorMap[useCase] || 'default';

  const authApiCallHookProps = {
    navigate,
    dispatch,
    setAuthUser,
    setAuthToken,
    authToken,
    redirectRoute: null,
  };

  // Carga unificada: lista y búsqueda usan el MISMO endpoint paginado
  // GET /users/paginated/:page/:pageSize con query params. Dos modos EXCLUYENTES:
  //  - Texto/useCase: ?search=&useCase=        (NUNCA se envía meetingNumber)
  //  - Actividad:     ?meetingNumber=&filterByMonth=true
  // Importante: mandar meetingNumber (aunque sea 0) hace que el backend lo trate como modo
  // actividad, por eso solo se incluye cuando es > 0.
  // La respuesta viene envuelta en { users: { count, page, pageSize, data } } en ambos modos.
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedMeetingNumber > 0) {
        params.set('meetingNumber', String(appliedMeetingNumber));
        params.set('filterByMonth', 'true');
      } else {
        if (appliedSearch) params.set('search', appliedSearch);
        if (appliedUseCase) params.set('useCase', appliedUseCase);
      }
      const qs = params.toString();
      const result = await authApiCall({
        method: 'GET',
        endpoint: endpoints.users,
        path: `/paginated/${page}/${rowsPerPage}${qs ? `?${qs}` : ''}`,
        ...authApiCallHookProps,
      });
      setuserList([...(result.data.users.data || [])]);
      setRowCount(result.data.users.count || 0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [page, rowsPerPage, appliedSearch, appliedUseCase, appliedMeetingNumber]);

  // Cross-ref opcional con el listado de suscripciones para enlazar a Stripe / ver detalles.
  // Ahora se cruza por `subscription.id` (el user trae `subscription: { id, status } | null`).
  const loadSubscriptions = async () => {
    const result = await authApiCall({
      method: 'GET',
      endpoint: endpoints.subscriptions,
      path: '',
      ...authApiCallHookProps,
    });
    const list = Array.isArray(result?.data) ? result.data : result?.data?.data ?? [];
    const map = {};
    list.forEach((s) => {
      map[s.id] = s;
    });
    setSubscriptionsById(map);
  };

  useEffect(() => {
    loadSubscriptions();
  }, []);

  // Aplica los filtros (o los limpia). El fetch se dispara vía el efecto de `applied*`.
  const handleSearch = ({ reset }) => {
    if (reset) {
      setFilterName('');
      setUseCaseFilter('');
      setMeetingPerMonthSearchValue(0);
      setAppliedSearch('');
      setAppliedUseCase('');
      setAppliedMeetingNumber(0);
      setPage(0);
      return;
    }
    // meetingNumber manda: si hay reuniones seleccionadas, es modo actividad (excluye texto/useCase).
    if (meetingsPerMonthSearchValue > 0) {
      setAppliedMeetingNumber(meetingsPerMonthSearchValue);
      setAppliedSearch('');
      setAppliedUseCase('');
    } else {
      setAppliedSearch(filterName.trim());
      setAppliedUseCase(useCaseFilter);
      setAppliedMeetingNumber(0);
    }
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = userList.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByEmailUsernameOrGivenName = async (event) => {
    setFilterName(event.target.value);
  };

  const handleMenuAction = (action, userId) => {
    switch (action) {
      case 'delete':
        setuserList((prev) => prev.filter((u) => u.id !== userId));
        setRowCount((prev) => Math.max(0, prev - 1));
        break;
      case 'show-metrics':
        setUserMetricsDialogOpen(true);
        setUserMetricsCurrentId(userId);
        break;
      case 'edit-user':
        setEditUser(userList.find((u) => u.id === userId));
        setEditUserDialogOpen(true);
        break;
      case 'open-subscription': {
        const target = userList.find((u) => u.id === userId);
        if (target?.subscription) openSeatDialog(target.subscription);
        break;
      }
      default:
        break;
    }
  };

  // Abre el drill-in de la sub. Abrimos optimista con lo que tengamos cacheado (mapa o { id, status })
  // y en paralelo pedimos la ficha COMPLETA al mismo endpoint que usa la tabla de suscripciones
  // (`/subscriptions/list?search=<uuid>`), para que la cabecera del drill-in no salga a medias
  // (createdAt, currentPeriodEnd, precio, billingInterval, stripeCustomerId…). El `GET /subscriptions`
  // que alimenta `subscriptionsById` es más ligero y no trae todos esos campos.
  const openSeatDialog = async (subscription) => {
    if (!subscription?.id) return;
    seatDialogDirty.current = false;
    setSeatDialogSub(subscriptionsById[subscription.id] || { id: subscription.id, status: subscription.status });

    try {
      const res = await authApiCall({
        method: 'GET',
        endpoint: endpoints.subscriptions,
        path: `/list?search=${encodeURIComponent(subscription.id)}&pageSize=1`,
        ...authApiCallHookProps,
      });
      const items = res?.data?.items || [];
      // Match exacto por id: `search` puede ser parcial, no nos fiamos del primer item a ciegas.
      const full = items.find((it) => it.id === subscription.id);
      if (full) {
        // Solo actualizamos si el diálogo sigue abierto en esta misma sub (evita pisar si el
        // admin cerró o abrió otra mientras tanto).
        setSeatDialogSub((prev) => (prev && prev.id === full.id ? full : prev));
        setSubscriptionsById((prev) => ({ ...prev, [full.id]: full }));
      }
    } catch (e) {
      // No-crítico: nos quedamos con la versión cacheada ya mostrada.
    }
  };

  const closeSeatDialog = () => {
    setSeatDialogSub(null);
    // Si hubo cambios de seats, refrescamos la tabla para reflejar plan/estado actualizados.
    if (seatDialogDirty.current) {
      seatDialogDirty.current = false;
      fetchUsers();
    }
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowCount) : 0;

  // Activación = haber procesado (summary_succeed) al menos una reunión. Se calcula SOLO sobre la
  // página cargada: el backend no devuelve este dato agregado y `userList` es la página actual.
  const activatedInPage = userList.filter((u) => isActivatedUser(u.statusCounts, u.deletedStatusCounts)).length;
  const activationRate = userList.length > 0 ? Math.round((activatedInPage / userList.length) * 100) : 0;

  return (
    <Page title="Usuarios">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Usuarios
          </Typography>
        </Stack>

        {(kpisRes || kpisLoading) && (
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} md={8}>
              {kpisLoading ? (
                <ChartCardSkeleton variant="line" />
              ) : (
                <UsersGraph
                  theme={theme}
                  monthlabels={kpisRes.monthlabels}
                  monthlyUsers={
                    kpisRes.withoutAdmins.users.byMonth.usersByMonth.data[
                      kpisRes.withoutAdmins.users.byMonth.usersByMonth.data.length - 1
                    ] -
                    kpisRes.withoutAdmins.users.byMonth.usersByMonth.data[
                      kpisRes.withoutAdmins.users.byMonth.usersByMonth.data.length - 2
                    ]
                  }
                  usersByMonth={kpisRes.withoutAdmins.users.byMonth.usersByMonth.data}
                  growthPercentageByMonth={kpisRes.withoutAdmins.users.byMonth.usersGrowthByMonth.data}
                  dailyAverageByMonth={kpisRes.withoutAdmins.users.byMonth.dailyAverageUsersByMonth.data.map((d) =>
                    d ? d.toFixed(2) : 0
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {kpisLoading ? (
                <ChartCardSkeleton variant="pie" />
              ) : (
                <UseCasePieChart theme={theme} useCases={kpisRes.withoutAdmins.users.useCases} />
              )}
            </Grid>
          </Grid>
        )}

        {/* Activación agregada por el backend sobre una cohorte de usuarios. Es la cifra "de
            verdad": el resumen que hay dentro de la Card solo mira la página cargada. */}
        <ActivationCohortCard />

        <Card>
          <UserListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByEmailUsernameOrGivenName}
            handleSearch={handleSearch}
            isLoading={isLoading}
            onCreateClick={() => setNewUserDialogOpen(true)}
            meetingsValue={meetingsPerMonthSearchValue}
            onMeetingsChange={setMeetingPerMonthSearchValue}
            meetingsOptions={MEETINGS_PRESETS}
            useCaseValue={useCaseFilter}
            onUseCaseChange={setUseCaseFilter}
            useCaseOptions={USE_CASE_OPTIONS}
          />

          {/* Activación de la PÁGINA actual (no del total): cuántos de los usuarios cargados han
              procesado ya una reunión. Ver `isActivatedUser` para qué cuenta como "procesada". */}
          {userList.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 3, pb: 2, color: 'text.secondary', flexWrap: 'wrap' }}
            >
              <Tooltip title="Una reunión cuenta como procesada cuando llega a «Resumen completado» (summary_succeed). Las etapas intermedias (audio o transcripción completadas) no cuentan. Se incluyen las reuniones borradas: haberlas borrado no deshace la activación.">
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ cursor: 'help' }}>
                  <Iconify
                    icon="eva:checkmark-circle-2-fill"
                    width={18}
                    height={18}
                    sx={{ color: activatedInPage > 0 ? 'success.main' : 'text.disabled' }}
                  />
                  <Typography variant="body2">
                    <strong>
                      {activatedInPage}/{userList.length}
                    </strong>{' '}
                    han procesado una reunión ({activationRate}%)
                  </Typography>
                </Stack>
              </Tooltip>
              <Typography variant="caption" color="text.disabled">
                · solo esta página
              </Typography>
            </Stack>
          )}

          <Scrollbar>
            {/* En móvil no forzamos los 800px: con Fecha/Actividad/Workspace ocultas la tabla cabe sola. */}
            <TableContainer sx={{ minWidth: { xs: 0, md: 800 } }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={rowCount}
                  numSelected={selected.length}
                  // onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {userList.map((row) => {
                    const {
                      id,
                      givenName,
                      email,
                      role,
                      workspace,
                      totalMeetings,
                      createdAt,
                      phoneNumber,
                      weeklyActivity,
                      lastMonthActivity,
                      useCase,
                      monthlyActivity,
                      subscription,
                      seat,
                      language,
                      statusCounts,
                      weeklyStatusCounts,
                      monthlyStatusCounts,
                      lastMonthStatusCounts,
                      // Reuniones borradas: gemelas de los conteos vivos, por ventana.
                      // El desglose por estado es el que tenían al borrarse.
                      deletedMeetings,
                      weeklyDeleted,
                      monthlyDeleted,
                      lastMonthDeleted,
                      deletedStatusCounts,
                      weeklyDeletedStatusCounts,
                      monthlyDeletedStatusCounts,
                      lastMonthDeletedStatusCounts,
                    } = row;
                    // El plan se pinta desde `seat`; el estado desde `subscription.status`.
                    // `subscription` y `seat` vienen juntos y ambos son null si el user no tiene
                    // NINGUNA sub viva: el listado ya filtra por estado, así que una sub cancelada
                    // o vencida deja de pintar plan (antes se colaba y parecía que seguía activo).
                    // Cross-ref opcional por `subscription.id` para enlazar a Stripe / ver detalles.
                    // Activo = ha procesado al menos una reunión (summary_succeed), vivas + borradas.
                    const processed = processedMeetingCount(statusCounts, deletedStatusCounts);
                    const isActivated = processed > 0;
                    // Se pinta junto a la actividad (y en el resumen móvil, donde esa columna se oculta).
                    const activationLabel = (
                      <Tooltip
                        title={
                          isActivated
                            ? `Activado: ha procesado ${processed} reunion${
                                processed === 1 ? '' : 'es'
                              } (resumen completado).`
                            : 'Sin activar: ninguna reunión con el resumen completado. Puede tener reuniones creadas, en curso o con error.'
                        }
                      >
                        <Label
                          variant="ghost"
                          color={isActivated ? 'success' : 'default'}
                          startIcon={isActivated ? <Iconify icon="eva:checkmark-circle-2-fill" /> : null}
                        >
                          {isActivated ? 'Activado' : 'Sin activar'}
                        </Label>
                      </Tooltip>
                    );
                    const sub = subscription?.id ? subscriptionsById[subscription.id] : null;
                    const subStatus = subscriptionStatus(subscription ? { status: subscription.status } : null);
                    const isItemSelected = selected.indexOf(givenName) !== -1;

                    return (
                      <TableRow
                        hover
                        key={id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        {/* FECHA */}
                        <TableCell align="left" sx={{ whiteSpace: 'nowrap', ...hideOnMobile }}>
                          <Typography variant="body2" color="text.secondary">
                            {createdAt ? new Date(createdAt).toLocaleDateString() : '—'}
                          </Typography>
                        </TableCell>

                        {/* USER */}
                        <TableCell align="left" sx={{ minWidth: 260 }}>
                          <Stack spacing={0.25}>
                            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                              <Tooltip title={`Click para copiar ID — ${id}`}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  onClick={() => copyToClipboard(id)}
                                  sx={{
                                    cursor: 'pointer',
                                    fontFamily: 'monospace',
                                    '&:hover': { color: 'text.primary' },
                                  }}
                                >
                                  {String(id).slice(0, 8)}
                                </Typography>
                              </Tooltip>
                              <Typography variant="subtitle2" noWrap>
                                {givenName || '—'}
                              </Typography>
                              {role === 'ADMIN' && (
                                <Tooltip title="Administrador">
                                  <Iconify
                                    icon="eva:shield-fill"
                                    width={16}
                                    height={16}
                                    sx={{ color: 'warning.main' }}
                                  />
                                </Tooltip>
                              )}
                              {role && (
                                <Label variant="ghost" color={getColor(useCase)}>
                                  {useCase ? sentenceCase(useCase) : '?'}
                                </Label>
                              )}
                              {language && (
                                <Tooltip title={languageName(language)}>
                                  <Label variant="ghost" color="default">
                                    {language}
                                  </Label>
                                </Tooltip>
                              )}
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                              <Tooltip title="Click para copiar email">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  onClick={() => copyToClipboard(email)}
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  {email}
                                </Typography>
                              </Tooltip>
                              {phoneNumber && (
                                <>
                                  <Typography variant="body2" color="text.disabled">
                                    ·
                                  </Typography>
                                  <Tooltip title="Click para copiar teléfono">
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      onClick={() => copyToClipboard(phoneNumber)}
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                                      {phoneNumber}
                                    </Typography>
                                  </Tooltip>
                                </>
                              )}
                            </Stack>
                            {/* Resumen solo-móvil: en xs/sm las columnas Fecha y Actividad están ocultas,
                                así que repetimos aquí el alta y el total de reuniones. */}
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ display: { xs: 'flex', md: 'none' }, color: 'text.secondary' }}
                            >
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Iconify icon="eva:calendar-outline" width={14} height={14} />
                                <Typography variant="caption">
                                  {createdAt ? new Date(createdAt).toLocaleDateString() : '—'}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.disabled">
                                ·
                              </Typography>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Iconify icon="eva:video-outline" width={14} height={14} />
                                <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {totalMeetings ?? 0} reunion{(totalMeetings ?? 0) === 1 ? '' : 'es'}
                                </Typography>
                                {deletedMeetings > 0 && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'error.main', fontVariantNumeric: 'tabular-nums' }}
                                  >
                                    −{deletedMeetings}
                                  </Typography>
                                )}
                              </Stack>
                              {activationLabel}
                            </Stack>
                          </Stack>
                        </TableCell>

                        {/* ACTIVITY */}
                        <TableCell align="left" sx={hideOnMobile}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            <ActivityCount
                              count={weeklyActivity}
                              statusCounts={weeklyStatusCounts}
                              deleted={weeklyDeleted}
                              deletedStatusCounts={weeklyDeletedStatusCounts}
                              title="Últimos 7 días · desglose por estado"
                              deletedTitle="Eliminadas (7d) · estado al borrarse"
                            />
                            <Typography variant="body2" color="text.disabled">
                              ·
                            </Typography>
                            <ActivityCount
                              count={monthlyActivity}
                              statusCounts={monthlyStatusCounts}
                              deleted={monthlyDeleted}
                              deletedStatusCounts={monthlyDeletedStatusCounts}
                              title="Mes actual · desglose por estado"
                              deletedTitle="Eliminadas (mes) · estado al borrarse"
                            />
                            <Typography variant="body2" color="text.disabled">
                              ·
                            </Typography>
                            <ActivityCount
                              count={lastMonthActivity}
                              statusCounts={lastMonthStatusCounts}
                              deleted={lastMonthDeleted}
                              deletedStatusCounts={lastMonthDeletedStatusCounts}
                              title="Mes anterior · desglose por estado"
                              deletedTitle="Eliminadas (mes ant.) · estado al borrarse"
                            />
                            <Typography variant="body2" color="text.disabled">
                              ·
                            </Typography>
                            <ActivityCount
                              count={totalMeetings}
                              statusCounts={statusCounts}
                              deleted={deletedMeetings}
                              deletedStatusCounts={deletedStatusCounts}
                              title="Histórico · desglose por estado"
                              deletedTitle="Eliminadas (histórico) · estado al borrarse"
                              bold
                            />
                            {activationLabel}
                          </Stack>
                        </TableCell>

                        {/* WORKSPACE */}
                        <TableCell align="left" sx={hideOnMobile}>
                          {workspace && Object.keys(workspace).length > 0 ? (
                            <Tooltip title="Click para copiar ID del workspace">
                              <Box
                                component="span"
                                onClick={() => copyToClipboard(workspace.id)}
                                sx={{ cursor: 'pointer', display: 'inline-flex' }}
                              >
                                <Label variant="ghost" color="default">
                                  {workspace.name}
                                </Label>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* SUSCRIPCIÓN */}
                        <TableCell align="left">
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                              {/* Sin `subscription` no hay drill-in posible (necesita el id de la
                                  sub), así que el chip solo es clicable si viene. */}
                              {seat ? (
                                <Tooltip
                                  title={
                                    subscription?.id
                                      ? 'Ver suscripción y gestionar seats'
                                      : 'Seat sin suscripción viva asociada'
                                  }
                                >
                                  <Box
                                    component="span"
                                    onClick={() => subscription?.id && openSeatDialog(subscription)}
                                    sx={{ cursor: subscription?.id ? 'pointer' : 'default', display: 'inline-flex' }}
                                  >
                                    <Label
                                      variant="outlined"
                                      color="primary"
                                      sx={{ cursor: subscription?.id ? 'pointer' : 'default' }}
                                    >
                                      {seat.plan?.displayName || seat.planSlug}
                                    </Label>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Sin ninguna suscripción viva (las canceladas o vencidas no cuentan).">
                                  <Label variant="ghost" color="default">
                                    Sin plan
                                  </Label>
                                </Tooltip>
                              )}
                              {subscription && (
                                <Tooltip title="Ver suscripción y gestionar seats">
                                  <Box
                                    component="span"
                                    onClick={() => openSeatDialog(subscription)}
                                    sx={{ cursor: 'pointer', display: 'inline-flex' }}
                                  >
                                    <Label variant="ghost" color={subStatus.color} sx={{ cursor: 'pointer' }}>
                                      {subStatus.label}
                                    </Label>
                                  </Box>
                                </Tooltip>
                              )}
                              {seat?.addonSlugs?.length > 0 && (
                                <Tooltip title={seat.addonSlugs.join(', ')}>
                                  <Label variant="ghost" color="info">
                                    +{seat.addonSlugs.length} addon{seat.addonSlugs.length > 1 ? 's' : ''}
                                  </Label>
                                </Tooltip>
                              )}
                            </Stack>
                            {sub && sub.stripeSubscriptionId && (
                              <Button
                                size="small"
                                variant="text"
                                href={stripeSubscriptionUrl(sub.stripeSubscriptionId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<Iconify icon="eva:external-link-outline" width={14} height={14} />}
                                sx={{ minWidth: 0, px: 0.5 }}
                              >
                                Ver en Stripe
                              </Button>
                            )}
                            {sub && !sub.stripeSubscriptionId && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  setSubDialogSubscription(sub);
                                  setSubDialogOpen(true);
                                }}
                                sx={{ minWidth: 0, px: 0.5 }}
                              >
                                Detalles
                              </Button>
                            )}
                          </Stack>
                        </TableCell>

                        {/* ACTIONS */}
                        <TableCell align="right">
                          <UserMoreMenu onMenuAction={handleMenuAction} user={row} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={TABLE_HEAD.length} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={rowCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
      <UserMetricsDialog
        userId={userMetricsCurrentId}
        open={userMetricsDialogOpen}
        onClose={setUserMetricsDialogOpen.bind(null, false)}
      />
      <CreateNewUserFormDialog open={newUserDialogOpen} onClose={setNewUserDialogOpen.bind(null, false)} />
      <SubscriptionDetailsDialog
        open={subDialogOpen}
        onClose={() => setSubDialogOpen(false)}
        subscription={subDialogSubscription}
        onSaved={(updated) => {
          if (updated && updated.id) {
            setSubscriptionsById((prev) => ({ ...prev, [updated.id]: { ...prev[updated.id], ...updated } }));
          }
        }}
      />
      <EditUserDialog
        open={editUserDialogOpen}
        onClose={() => setEditUserDialogOpen(false)}
        user={editUser}
        onSaved={(updated) => {
          if (!updated?.id) return;
          setuserList((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
        }}
      />
      <SubscriptionUsersDialog
        open={Boolean(seatDialogSub)}
        subscription={seatDialogSub}
        onClose={closeSeatDialog}
        onUpdated={(updated) => {
          seatDialogDirty.current = true;
          if (updated && updated.id) {
            setSubscriptionsById((prev) => ({ ...prev, [updated.id]: { ...prev[updated.id], ...updated } }));
          }
        }}
      />
    </Page>
  );
}
